import { useEffect, useState } from "react"
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, List, ListItem, ListItemText, Snackbar } from "@mui/material"
import { formatArtifactSize } from "helpers/formatters/file-size"
import type { PendingBatch, PendingSave } from "./artifact-save-request"

/**
 * The gate on an artifact's export (APP-3495). The app cannot tell a real user click inside the
 * artifact from the artifact firing the save on its own, so it asks — naming every file, its type and
 * its size, because that is what the user is being asked to agree to.
 */

/**
 * Save stays inert for this long after the dialog appears. The artifact chooses the moment the dialog
 * mounts, so it can put Save under a cursor that is already parked where MUI centres it. Long enough to
 * break that, short enough that a user reaching for the button never notices.
 */
const SAVE_ARM_DELAY_MS = 400

interface ArtifactSaveDialogProps {
  pending: PendingBatch | null
  onConfirm: () => void
  onCancel: () => void
}

function describeFile({ filename, contentType, sizeBytes }: PendingSave): string {
  return `${filename} (${contentType}, ${formatArtifactSize(sizeBytes)})`
}

export function ArtifactSaveDialog({ pending, onConfirm, onCancel }: ArtifactSaveDialogProps) {
  // The pending request is cleared the instant the user decides, but the dialog animates closed after
  // that. Rendering straight from `pending` would blank the filename mid-fade, so the last request
  // shown is mirrored here and outlives it.
  // Adjusted during render rather than in an effect: React re-renders immediately and the guard stops
  // it recurring, where an effect would schedule a second pass every time a request arrives.
  const [lastShown, setLastShown] = useState<PendingBatch | null>(null)
  const [isArmed, setIsArmed] = useState(false)
  if (pending != null && pending !== lastShown) {
    setLastShown(pending)
    // Re-armed, not just armed once: a batch grows while the prompt is up, and Save must never be live
    // over a list the user has not had a moment to read.
    setIsArmed(false)
  }
  const batch = pending ?? lastShown
  const files = batch?.files ?? []
  const isBatch = files.length > 1

  useEffect(() => {
    if (pending == null) return undefined
    // Armed on a timer rather than synchronously in the effect: the delay is the point, and the
    // synchronous form is what `react-hooks/set-state-in-effect` forbids.
    const timer = setTimeout(() => setIsArmed(true), SAVE_ARM_DELAY_MS)
    return () => clearTimeout(timer)
  }, [pending])

  const description = files.length === 0
    ? ""
    : isBatch
      ? `This artifact wants to save ${files.length} files (${formatArtifactSize(batch?.totalBytes ?? 0)} in total).`
      : `This artifact wants to save ${describeFile(files[0])}.`

  return (
    <Dialog open={pending != null} onClose={onCancel} aria-labelledby="artifact-save-title">
      <DialogTitle id="artifact-save-title">{isBatch ? "Save these files?" : "Save this file?"}</DialogTitle>
      <DialogContent>
        <DialogContentText>{description}</DialogContentText>
        {isBatch && (
          <List dense disablePadding>
            {/* Keyed with the position too: two files in one batch can carry the same name. */}
            {files.map((file, index) => (
              <ListItem key={`${index}-${file.filename}`} disableGutters>
                <ListItemText primary={describeFile(file)} />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" disabled={!isArmed} data-testid="artifact-save-confirm">Save</Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Says that a save was refused, and nothing else. The message comes from the broker and never repeats
 * anything the artifact supplied — an artifact can provoke a refusal deliberately, and echoing its
 * filename here would let it write text of its choosing into app chrome.
 */
interface ArtifactRefusalNoticeProps {
  refusal: string | null
  onDismiss: () => void
}

export function ArtifactRefusalNotice({ refusal, onDismiss }: ArtifactRefusalNoticeProps) {
  return (
    <Snackbar open={refusal != null} autoHideDuration={8000} onClose={onDismiss}>
      <Alert severity="warning" onClose={onDismiss}>{refusal}</Alert>
    </Snackbar>
  )
}
